/**
 * « COMPARER À LA PRÉCÉDENTE » (24/10/2026) — LE MODÈLE, PUR.
 *
 * La fiche d'une soirée ouvre en un clic la comparaison avec SA soirée
 * précédente comparable : `/soirees/compare?a=<soirée>&b=<précédente>`, que
 * la page de comparaison lit déjà (App.tsx passe `a` et `b` à ComparePage).
 *
 * ═══ QUELLE PRÉCÉDENTE ═══
 *
 * La règle existante, celle de la tuile Performance en mode « Soirée
 * précédente comparable » : la plus récente de la même famille, sinon du
 * même jour de semaine, sinon la précédente tout court, parmi les soirées
 * maintenues antérieures (`findComparables`, mode `PREVIOUS_COMPARABLE`,
 * packages/core/src/ticketing/compare.ts). Le serveur la tranche sur la
 * route existante `GET /api/v1/soirees/:id/compare` : aucune route nouvelle,
 * aucune règle recopiée ici. Une soirée hors analyses (association, réglage
 * éteint) n'a pas de précédente : le serveur dit pourquoi, on le redit.
 *
 * Aucune écriture : une lecture, puis une navigation.
 */

export const LIBELLE_COMPARER_PRECEDENTE = 'Comparer à la précédente'
export const RAISON_RECHERCHE = 'Recherche de la soirée précédente comparable…'
export const RAISON_AUCUNE = 'Aucune soirée précédente comparable dans l’historique connu.'
/** Le libellé du droit `soiree.read` (migrations/1756900000001_permission-v2-mapping.sql). */
export const RAISON_REFUS = 'La comparaison demande le droit « Consulter les soirées ».'
export const RAISON_INCONNUE = 'Soirée inconnue.'
export const RAISON_ERREUR = 'La soirée précédente n’a pas pu être lue. Réessayez dans un instant.'

/** La lecture du serveur : la même route que la tuile Performance, dans le mode « précédente comparable ». */
export function urlPrecedente(id: string): string {
  return `/api/v1/soirees/${id}/compare?mode=PREVIOUS_COMPARABLE`
}

/** La page d'arrivée : la comparaison A/B, les deux soirées posées. */
export function cheminComparerPrecedente(id: string, precedenteId: string): string {
  return `/soirees/compare?a=${id}&b=${precedenteId}`
}

/** Ce que le geste garde de la réponse : la référence, et la raison d'une soirée hors analyses. */
export interface ReponseComparaison {
  comparison?: { reference?: { id: string; name: string; starts_at: string } | null } | null
  hors_analyses?: string
}

export type LecturePrecedente =
  | { etat: 'trouvee'; precedente: { id: string; name: string; starts_at: string } }
  | { etat: 'aucune' | 'erreur'; raison: string }

/**
 * Le verdict d'une lecture. `aucune` : il n'y a rien à comparer, la raison
 * le dit ; `erreur` : la lecture a échoué. Une référence égale à la soirée
 * elle-même compte comme « aucune » : se comparer à soi ne dit rien.
 */
export function lecturePrecedente(status: number, data: ReponseComparaison | null | undefined, id: string): LecturePrecedente {
  if (status === 403) return { etat: 'erreur', raison: RAISON_REFUS }
  if (status === 404) return { etat: 'erreur', raison: RAISON_INCONNUE }
  if (status !== 200 || !data || typeof data !== 'object' || !data.comparison || typeof data.comparison !== 'object') {
    return { etat: 'erreur', raison: RAISON_ERREUR }
  }
  if (typeof data.hors_analyses === 'string' && data.hors_analyses !== '') return { etat: 'aucune', raison: data.hors_analyses }
  const r = data.comparison.reference
  if (!r || typeof r.id !== 'string' || r.id === '' || r.id === id) return { etat: 'aucune', raison: RAISON_AUCUNE }
  return {
    etat: 'trouvee',
    precedente: { id: r.id, name: typeof r.name === 'string' ? r.name : '', starts_at: typeof r.starts_at === 'string' ? r.starts_at : '' },
  }
}
